import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { generatePath } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import * as yup from 'yup';

import { VIEW_PATHS_ACCESS_SOLUTIONS_PLATFORM } from '@hcs/app-configs';
import { useAccount } from '@hcs/authn';
import {
  BuildingIcon,
  Button,
  Card,
  Checkbox,
  CompleteIcon,
  FormError,
  Input,
  StateDropdown,
  UserGroupIcon,
  UserProfileIcon,
} from '@hcs/design-system';
import {
  MlsRegistrationRequest,
  MlsRegistrationResponse,
  MyStateMlsSignupFields,
  MyStateMlsSignupFormFields,
} from '@hcs/types';
import { SOLUTIONS_URL } from '@hcs/urls';

import { useUserAgentMlsPartner } from '../../hooks';
import { useRegisterAgentForMls } from '../../hooks/useRegisterAgentForMls';

import styles from './MyStateMlsSignup.module.css';

const MYSTATE_MLS_CODE = 'MYSTATE';
const REQUIRED_MESSAGE = 'Required';
const MAX_NAME_LENGTH = 64;
const MAX_EMAIL_LENGTH = 128;
const MAX_ADDRESS_LENGTH = 255;
const MAX_CITY_LENGTH = 64;
const APPROVAL_REQUIRED_MESSAGE =
  'You must acknowledge brokerage approval to continue';
const BROKER_APPROVE_ALL_URL_OVERRIDE =
  SOLUTIONS_URL +
  generatePath(VIEW_PATHS_ACCESS_SOLUTIONS_PLATFORM.BROKER_AUTHORIZATION, {
    scope: 'office',
  });
const BROKER_APPROVE_AGENT_URL_OVERRIDE =
  SOLUTIONS_URL +
  generatePath(VIEW_PATHS_ACCESS_SOLUTIONS_PLATFORM.BROKER_AUTHORIZATION, {
    scope: 'agent',
  });

const BROKER_DENY_AGENT_URL_OVERRIDE =
  SOLUTIONS_URL +
  generatePath(VIEW_PATHS_ACCESS_SOLUTIONS_PLATFORM.BROKER_AUTHORIZATION, {
    scope: 'agent',
  });

const yupSchema: yup.ObjectSchema<MyStateMlsSignupFormFields> = yup
  .object()
  .shape({
    [MyStateMlsSignupFields.FirstName]: yup
      .string()
      .max(MAX_NAME_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.LastName]: yup
      .string()
      .max(MAX_NAME_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.Email]: yup
      .string()
      .email('Invalid email')
      .max(MAX_EMAIL_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.OfficeName]: yup
      .string()
      .max(MAX_NAME_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.StreetAddress]: yup
      .string()
      .max(MAX_ADDRESS_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.City]: yup
      .string()
      .max(MAX_CITY_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.State]: yup
      .string()
      .length(2, 'Select a state')
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.Zip]: yup
      .string()
      .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP')
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.OfficeContactEmail]: yup
      .string()
      .email('Invalid email')
      .max(MAX_EMAIL_LENGTH)
      .required(REQUIRED_MESSAGE),
    [MyStateMlsSignupFields.BrokerageApproval]: yup
      .boolean()
      .oneOf([true], APPROVAL_REQUIRED_MESSAGE)
      .required(APPROVAL_REQUIRED_MESSAGE),
  });

const dataHcName = 'my-state-mls-signup';

export interface MyStateMlsSignupProps {
  className?: string;
  userId: number;
  hcMlsId: number;
  agentMlsPartnerId: string;
  onSuccess?: (response: MlsRegistrationResponse) => void;
  onDone?: VoidFunction;
}

interface LabeledFieldProps {
  label: string;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
}

const LabeledField = ({
  label,
  required,
  htmlFor,
  children,
}: LabeledFieldProps) => (
  <div className={styles.Field}>
    <label className={styles.FieldLabel} htmlFor={htmlFor}>
      {label}
      {required && <span className={styles.RequiredMark}>*</span>}
    </label>
    {children}
  </div>
);

const WarningTriangleIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 3 1.5 21h21L12 3Zm0 14.25a1.05 1.05 0 1 1 0-2.1 1.05 1.05 0 0 1 0 2.1Zm.9-3.9h-1.8V9h1.8v4.35Z"
      fill="currentColor"
    />
  </svg>
);

interface RegistrationSubmittedProps {
  variant: 'success' | 'warning';
  onDone?: VoidFunction;
}

const SUCCESS_STEPS = [
  'Your brokerage will review and respond to the approval request.',
  'Once approved, your listings will be enabled for submission to My State MLS.',
];

const WARNING_STEPS = [
  'Our team will contact your brokerage directly to obtain brokerage approval.',
  'Once approved, your listings will be enabled for submission to My State MLS.',
];

const RegistrationSubmitted = ({
  variant,
  onDone,
}: RegistrationSubmittedProps) => {
  const isSuccess = variant === 'success';
  return (
    <>
      <div className={styles.SubmittedHeader}>
        {isSuccess ? (
          <CompleteIcon dataHcName={`${dataHcName}-submitted-icon`} />
        ) : (
          <WarningTriangleIcon className={styles.WarningIcon} />
        )}
        <div className={styles.SubmittedTitle}>Registration submitted</div>
      </div>
      <div className={styles.SubmittedBody}>
        <div
          className={classNames(styles.SubmittedBanner, {
            [styles.Success]: isSuccess,
            [styles.Warning]: !isSuccess,
          })}
          data-hc-name={`${dataHcName}-submitted-banner-${variant}`}
        >
          <div className={styles.SubmittedBannerIcon}>
            {isSuccess ? (
              <CompleteIcon
                dataHcName={`${dataHcName}-submitted-banner-icon`}
              />
            ) : (
              <WarningTriangleIcon className={styles.WarningIcon} />
            )}
          </div>
          <div className={styles.SubmittedBannerText}>
            {isSuccess ? (
              <>
                <strong>Brokerage approval request sent.</strong> We've
                successfully contacted your brokerage to request approval for
                your listings on My State MLS.
              </>
            ) : (
              <>
                <strong>Automatic brokerage contact unsuccessful.</strong> We
                were unable to automatically reach your brokerage for approval.
                The request has been submitted for manual brokerage approval.
              </>
            )}
          </div>
        </div>
        <div>
          <div className={styles.WhatNextLabel}>What happens next</div>
          <ol className={styles.WhatNextList}>
            {(isSuccess ? SUCCESS_STEPS : WARNING_STEPS).map((step) => (
              <li key={step} className={styles.WhatNextItem}>
                {step}
              </li>
            ))}
          </ol>
        </div>
      </div>
      {onDone && (
        <div className={styles.SubmittedFooter}>
          <Button
            dataHcName={`${dataHcName}-done-button`}
            type="button"
            secondary
            label="Done"
            onClick={onDone}
          />
        </div>
      )}
    </>
  );
};

export const MyStateMlsSignup = ({
  className,
  userId,
  hcMlsId,
  agentMlsPartnerId,
  onSuccess,
  onDone,
}: MyStateMlsSignupProps) => {
  const { data: account } = useAccount();
  const { data: userAgentMlsPartner } = useUserAgentMlsPartner();
  const DEFAULT_VALUES: MyStateMlsSignupFormFields = {
    [MyStateMlsSignupFields.FirstName]: account?.user.firstName || '',
    [MyStateMlsSignupFields.LastName]: account?.user.lastName || '',
    [MyStateMlsSignupFields.Email]:
      userAgentMlsPartner?.agentEmailAddress || '',
    [MyStateMlsSignupFields.OfficeName]: '',
    [MyStateMlsSignupFields.StreetAddress]: '',
    [MyStateMlsSignupFields.City]: '',
    [MyStateMlsSignupFields.State]: '',
    [MyStateMlsSignupFields.Zip]: '',
    [MyStateMlsSignupFields.OfficeContactEmail]: '',
    [MyStateMlsSignupFields.BrokerageApproval]: false,
  };
  const form = useForm<MyStateMlsSignupFormFields>({
    resolver: yupResolver(yupSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [submittedResponse, setSubmittedResponse] =
    useState<MlsRegistrationResponse | null>(null);
  const [submittedVariant, setSubmittedVariant] = useState<
    'success' | 'warning'
  >('success');

  const { mutate: registerAgent, isPending } = useRegisterAgentForMls({
    onError(error) {
      setErrorMessage(
        error.response?.data.message ||
          'Failed to register with My State MLS. Please try again.',
      );
    },
    onSuccess: (response) => {
      setErrorMessage(undefined);
      setSubmittedResponse(response);
      onSuccess?.(response);
      setSubmittedVariant(
        response.brokerApprovalEmailSent ? 'success' : 'warning',
      );
    },
  });

  const handleSubmit = (data: MyStateMlsSignupFormFields) => {
    const payload: MlsRegistrationRequest = {
      mls: MYSTATE_MLS_CODE,
      hcMlsId,
      agentMlsPartnerId,
      approveAllUrl: BROKER_APPROVE_ALL_URL_OVERRIDE,
      approveUrl: BROKER_APPROVE_AGENT_URL_OVERRIDE,
      denyUrl: BROKER_DENY_AGENT_URL_OVERRIDE,
      broker: {
        officeName: data[MyStateMlsSignupFields.OfficeName],
        address: data[MyStateMlsSignupFields.StreetAddress],
        city: data[MyStateMlsSignupFields.City],
        state: data[MyStateMlsSignupFields.State],
        zipcode: data[MyStateMlsSignupFields.Zip],
        contactEmail: data[MyStateMlsSignupFields.OfficeContactEmail],
      },
      agent: {
        firstName: data[MyStateMlsSignupFields.FirstName],
        lastName: data[MyStateMlsSignupFields.LastName],
        emailAddress: data[MyStateMlsSignupFields.Email],
      },
    };
    registerAgent(payload);
  };

  const handleClear = () => {
    setErrorMessage(undefined);
    form.reset(DEFAULT_VALUES);
  };

  if (submittedResponse) {
    return (
      <Card
        dataHcName={`${dataHcName}-submitted`}
        className={classNames(styles.MyStateMlsSignup, className)}
        noPadding
      >
        <RegistrationSubmitted variant={submittedVariant} onDone={onDone} />
      </Card>
    );
  }

  return (
    <Card
      dataHcName={dataHcName}
      className={classNames(styles.MyStateMlsSignup, className)}
      noPadding
    >
      <div className={styles.Header}>
        <div className={styles.HeaderIcon}>
          <UserGroupIcon dataHcName={`${dataHcName}-header-icon`} />
        </div>
        <div className={styles.HeaderText}>
          <div className={styles.HeaderTitle}>Register agent & office</div>
          <div className={styles.HeaderDescription}>
            Submit your Agent information and associated office or brokerage to
            My State MLS. All required fields must be completed for successful
            registration.
          </div>
        </div>
      </div>
      {errorMessage && (
        <FormError
          dataHcName={`${dataHcName}-errors`}
          className={styles.Error}
          value={<div>{errorMessage}</div>}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
          <section
            className={styles.Section}
            data-hc-name={`${dataHcName}-agent-section`}
          >
            <div className={styles.SectionHeader}>
              <div className={styles.HeaderIcon}>
                <UserProfileIcon dataHcName={`${dataHcName}-agent-icon`} />
              </div>
              <div className={styles.SectionTitle}>Agent</div>
            </div>

            <div className={styles.FieldGroup}>
              <div className={styles.SubsectionLabel}>Agent Identity</div>
              <div className={styles.Row}>
                <Controller
                  name={MyStateMlsSignupFields.FirstName}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <LabeledField
                      label="First name"
                      required
                      htmlFor={`${dataHcName}-${field.name}`}
                    >
                      <Input
                        dataHcName={`${dataHcName}-${field.name}`}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="e.g. Jim"
                        error={fieldState.error?.message}
                      />
                    </LabeledField>
                  )}
                />
                <Controller
                  name={MyStateMlsSignupFields.LastName}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <LabeledField
                      label="Last name"
                      required
                      htmlFor={`${dataHcName}-${field.name}`}
                    >
                      <Input
                        dataHcName={`${dataHcName}-${field.name}`}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="e.g. Smith"
                        error={fieldState.error?.message}
                      />
                    </LabeledField>
                  )}
                />
              </div>
            </div>

            <div className={styles.FieldGroup}>
              <div className={styles.SubsectionLabel}>Contact Information</div>
              <Controller
                name={MyStateMlsSignupFields.Email}
                control={form.control}
                render={({ field, fieldState }) => (
                  <LabeledField
                    label="Email address"
                    required
                    htmlFor={`${dataHcName}-${field.name}`}
                  >
                    <Input
                      dataHcName={`${dataHcName}-${field.name}`}
                      type="email"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="e.g. jim@example.com"
                      error={fieldState.error?.message}
                    />
                  </LabeledField>
                )}
              />
            </div>
          </section>

          <section
            className={styles.Section}
            data-hc-name={`${dataHcName}-office-section`}
          >
            <div className={styles.SectionHeader}>
              <div className={styles.HeaderIcon}>
                <BuildingIcon dataHcName={`${dataHcName}-office-icon`} />
              </div>
              <div className={styles.SectionTitle}>Office / brokerage</div>
            </div>

            <div className={styles.FieldGroup}>
              <div className={styles.SubsectionLabel}>Office Identity</div>
              <Controller
                name={MyStateMlsSignupFields.OfficeName}
                control={form.control}
                render={({ field, fieldState }) => (
                  <LabeledField
                    label="Office name"
                    required
                    htmlFor={`${dataHcName}-${field.name}`}
                  >
                    <Input
                      dataHcName={`${dataHcName}-${field.name}`}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="e.g. Valley Realty"
                      error={fieldState.error?.message}
                    />
                  </LabeledField>
                )}
              />
              <Controller
                name={MyStateMlsSignupFields.OfficeContactEmail}
                control={form.control}
                render={({ field, fieldState }) => (
                  <LabeledField
                    label="Office contact email"
                    required
                    htmlFor={`${dataHcName}-${field.name}`}
                  >
                    <Input
                      dataHcName={`${dataHcName}-${field.name}`}
                      type="email"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="e.g. broker@valleyrealty.com"
                      error={fieldState.error?.message}
                    />
                  </LabeledField>
                )}
              />
            </div>

            <div className={styles.FieldGroup}>
              <div className={styles.SubsectionLabel}>Office Address</div>
              <Controller
                name={MyStateMlsSignupFields.StreetAddress}
                control={form.control}
                render={({ field, fieldState }) => (
                  <LabeledField
                    label="Street address"
                    required
                    htmlFor={`${dataHcName}-${field.name}`}
                  >
                    <Input
                      dataHcName={`${dataHcName}-${field.name}`}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="e.g. 123 Main St"
                      error={fieldState.error?.message}
                    />
                  </LabeledField>
                )}
              />
              <div className={styles.CityStateZipRow}>
                <Controller
                  name={MyStateMlsSignupFields.City}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <div className={styles.City}>
                      <LabeledField
                        label="City"
                        required
                        htmlFor={`${dataHcName}-${field.name}`}
                      >
                        <Input
                          dataHcName={`${dataHcName}-${field.name}`}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="e.g. Portland"
                          error={fieldState.error?.message}
                        />
                      </LabeledField>
                    </div>
                  )}
                />
                <Controller
                  name={MyStateMlsSignupFields.State}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <div className={styles.State}>
                      <LabeledField
                        label="State"
                        required
                        htmlFor={`${dataHcName}-${field.name}`}
                      >
                        <StateDropdown
                          dataHcName={`${dataHcName}-${field.name}`}
                          name={field.name}
                          value={field.value}
                          onSelect={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="—"
                          error={fieldState.error?.message}
                        />
                      </LabeledField>
                    </div>
                  )}
                />
                <Controller
                  name={MyStateMlsSignupFields.Zip}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <div className={styles.Zip}>
                      <LabeledField
                        label="ZIP"
                        required
                        htmlFor={`${dataHcName}-${field.name}`}
                      >
                        <Input
                          dataHcName={`${dataHcName}-${field.name}`}
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="e.g. 97201"
                          error={fieldState.error?.message}
                        />
                      </LabeledField>
                    </div>
                  )}
                />
              </div>
            </div>
          </section>

          <div className={styles.AgreementSection}>
            <Controller
              name={MyStateMlsSignupFields.BrokerageApproval}
              control={form.control}
              render={({ field, fieldState }) => (
                <>
                  <div
                    className={styles.AgreementCallout}
                    data-hc-name={`${dataHcName}-brokerage-approval-callout`}
                  >
                    <Checkbox
                      dataHcName={`${dataHcName}-${field.name}`}
                      checked={field.value}
                      onChange={field.onChange}
                      label={
                        <span className={styles.AgreementText}>
                          Brokerage approval required. I understand that my
                          brokerage must be contacted for approval before any
                          listings can be submitted to My State MLS.
                        </span>
                      }
                    />
                  </div>
                  {fieldState.error?.message && (
                    <FormError
                      dataHcName={`${dataHcName}-${field.name}-error`}
                      value={fieldState.error.message}
                    />
                  )}
                </>
              )}
            />
          </div>

          <div className={styles.Footer}>
            <div className={styles.RequiredLegend}>
              <span className={styles.RequiredMark}>*</span> Required fields
            </div>
            <div className={styles.FooterActions}>
              <Button
                dataHcName={`${dataHcName}-clear-button`}
                type="button"
                secondary
                label="Clear"
                onClick={handleClear}
                disabled={isPending}
              />
              <Button
                dataHcName={`${dataHcName}-submit-button`}
                type="submit"
                primary
                label="Submit to My State MLS"
                disabled={isPending}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
};
